import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import { DropTargetSpacer } from '@juvare/widget-ui/DropTargetSpacer';
import { walkWidgetPropsTree } from '@juvare/widget-ui/Widget';
import { WidgetComponent } from '@juvare/widget-ui/WidgetComponent';
import { WidgetEditorContext } from '@juvare/widget-ui/WidgetEditorContext';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import { Badge, Box, FormControl, FormControlLabel, Radio, RadioGroup, Tab, Tabs } from '@mui/material';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { callEventFunction, defaultWidgetLabelProvider, getChildWidgetStyle, getVisibleError, handleWidgetUpdate, isErrorVisible, joinLabels, makeNewConfigUnique, renderForm, } from '../WidgetForm/Utils';
import { WidgetHandler } from '../WidgetHandler';
import { type } from './types';
const WidgetFormTabs = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const editorContext = React.useContext(WidgetEditorContext);
    let activeTabValue = props.internalData.activeTab;
    let activeTab;
    if (activeTabValue != null && activeTabValue >= 0 && activeTabValue < props.config.tabs.length) {
        activeTab = props.config.tabs[activeTabValue];
    }
    else if (props.config.defaultTab && props.config.defaultTab >= 0 && props.config.defaultTab < props.config.tabs.length) {
        activeTabValue = props.config.defaultTab;
        activeTab = props.config.tabs[activeTabValue];
    }
    else if (props.config.tabs.length > 0) {
        activeTabValue = 0;
        activeTab = props.config.tabs[0];
    }
    else {
        activeTabValue = false;
        activeTab = undefined;
    }
    let activeTabChildProps;
    let activeTabFirstChildPropIndex;
    if (props.childProps && activeTab) {
        activeTabChildProps = props.childProps.filter((p, index) => {
            const found = activeTab === null || activeTab === void 0 ? void 0 : activeTab.children.includes(p.config);
            if (found && activeTabFirstChildPropIndex == null) {
                activeTabFirstChildPropIndex = index;
            }
            return found;
        });
        if (!editorContext.editMode) {
            activeTabChildProps = activeTabChildProps.filter((p) => !p.hidden);
        }
    }
    const childrenHaveErrors = (tab) => {
        let error = false;
        if (props.childProps) {
            for (const childProps of props.childProps) {
                if (tab.children.includes(childProps.config)) {
                    walkWidgetPropsTree(childProps, (p) => {
                        if (isErrorVisible(p)) {
                            error = true;
                        }
                    });
                }
                if (error) {
                    break;
                }
            }
        }
        return error;
    };
    const display = (_b = (_a = props.config.style) === null || _a === void 0 ? void 0 : _a.display) !== null && _b !== void 0 ? _b : 'tabs';
    const spacing = (_d = (_c = props.config.style) === null || _c === void 0 ? void 0 : _c.spacing) !== null && _d !== void 0 ? _d : 16;
    return (React.createElement(Box, null,
        display === 'tabs' && (React.createElement(Box, { sx: { borderBottom: 1, borderColor: 'divider' } },
            React.createElement(Tabs, { variant: 'scrollable', value: activeTabValue, onChange: (event, value) => {
                    var _a;
                    props.internalData.activeTab = value;
                    if ((_a = props.config.event) === null || _a === void 0 ? void 0 : _a.onChangeTab) {
                        callEventFunction(props, 'onChangeTab', 'tab, handler', value, new WidgetHandler(props));
                    }
                    props.update();
                    renderForm(props);
                } }, props.config.tabs.map((tab, index) => {
                const error = childrenHaveErrors(tab);
                if (error) {
                    return (React.createElement(Tab, { key: index, value: index, label: React.createElement(Badge, { color: 'error', variant: 'dot' }, tab.label), tabIndex: props.editMode ? -1 : undefined, "data-test": 'tab' }));
                }
                else {
                    return (React.createElement(Tab, { key: index, value: index, label: tab.label, tabIndex: props.editMode ? -1 : undefined, "data-test": 'tab' }));
                }
            })))),
        display === 'radio' && (React.createElement(FormControl, { sx: { width: (_e = props.config.style) === null || _e === void 0 ? void 0 : _e.width }, fullWidth: true },
            React.createElement(RadioGroup, { row: true, value: activeTabValue === false ? '' : activeTabValue, onChange: (event) => {
                    var _a;
                    const value = parseFloat(event.target.value);
                    props.internalData.activeTab = value;
                    if ((_a = props.config.event) === null || _a === void 0 ? void 0 : _a.onChangeTab) {
                        callEventFunction(props, 'onChangeTab', 'tab, handler', value, new WidgetHandler(props));
                    }
                    props.update();
                    renderForm(props);
                } }, props.config.tabs.map((tab, index) => {
                const error = childrenHaveErrors(tab);
                if (error) {
                    return (React.createElement(FormControlLabel, { key: index, value: index, control: React.createElement(Radio, { tabIndex: props.editMode ? -1 : undefined, "data-test": 'tab' }), label: React.createElement(Badge, { color: 'error', variant: 'dot' }, tab.label) }));
                }
                else {
                    return (React.createElement(FormControlLabel, { key: index, value: index, control: React.createElement(Radio, { tabIndex: props.editMode ? -1 : undefined, "data-test": 'tab' }), label: tab.label }));
                }
            })))),
        !props.editorOverlay && activeTabChildProps && (React.createElement(Box, { style: {
                paddingTop: display === 'radio' ? spacing : undefined,
                width: (_f = props.config.style) === null || _f === void 0 ? void 0 : _f.width,
            }, p: display === 'tabs' ? 3 : undefined, "data-editor-path": 'tabs.#' + activeTabValue + '.children' },
            activeTabChildProps.length === 0 && React.createElement(DropTargetSpacer, { spacing: spacing, first: true, last: true, editorPath: '#0+' }),
            activeTabChildProps.length > 0 &&
                activeTabChildProps.map((childProps, index) => (React.createElement(React.Fragment, { key: childProps.id },
                    React.createElement(DropTargetSpacer, { spacing: spacing, first: index === 0, last: false, editorPath: '#' + index + '+' }),
                    React.createElement(Box, { style: getChildWidgetStyle(childProps), "data-editor-path": '#' + index, "data-widget": childProps.dataPath, "data-visible-error": getVisibleError(childProps) ? 'true' : undefined },
                        React.createElement(WidgetComponent, Object.assign({}, childProps, { renderData: props.renderData })))))),
            activeTabChildProps.length > 0 && (React.createElement(DropTargetSpacer, { spacing: spacing, first: false, last: true, editorPath: '#' + activeTabChildProps.length + '+' }))))));
};
const WidgetFormTabsEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'Tabs' }, buildGeneralTab(props, {
            onSubmit: (data) => {
                data.tabs.forEach((tab) => {
                    if (!tab.children) {
                        tab.children = [];
                    }
                });
                return data;
            },
            children: [
                {
                    type: 'form.dataGrid',
                    key: 'tabs',
                    label: 'Tabs',
                    children: [
                        {
                            type: 'form.text',
                            key: 'label',
                            label: 'Label',
                            validate: {
                                required: true,
                            },
                        },
                    ],
                    validate: {
                        required: true,
                    },
                },
                {
                    type: 'form.number',
                    key: 'defaultTab',
                    label: 'Default Tab',
                    validate: {
                        min: 0,
                    },
                    style: {
                        width: 200,
                    },
                },
            ],
        }), buildHiddenTab(props), buildStyleTab(props, {
            onLoad: (data) => {
                if (!data.style) {
                    data.style = {};
                }
                if (!data.style.display) {
                    data.style.display = 'tabs';
                }
                return data;
            },
            onSubmit: (data) => {
                var _a;
                if (data.style) {
                    if (((_a = data.style) === null || _a === void 0 ? void 0 : _a.display) === 'tabs') {
                        delete data.style.display;
                    }
                    if (Object.keys(data.style).length === 0) {
                        delete data.style;
                    }
                }
                return data;
            },
            children: [
                {
                    type: 'form.select',
                    key: 'style.display',
                    label: 'Display',
                    values: [
                        { value: 'tabs', label: 'Tabs' },
                        { value: 'radio', label: 'Radio' },
                    ],
                    style: {
                        width: 300,
                    },
                },
                {
                    type: 'form.number',
                    key: 'style.spacing',
                    label: 'Spacing',
                    validate: {
                        min: 0,
                    },
                    style: {
                        width: 200,
                    },
                },
            ],
        }), buildEventTab(props, { eventOnChangeTab: true })), editorOverlay: React.createElement(WidgetFormTabs, Object.assign({}, props, { editorOverlay: true })) }),
        React.createElement(WidgetFormTabs, Object.assign({}, props))));
};
export const widgetFormTabs = {
    type,
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        if (props.childProps) {
            return props.childProps.flatMap((childProp) => {
                var _a;
                if (childProp.widget.getLabelValueAsTextPairs) {
                    const tabLabel = (_a = props.config.tabs.find((tab) => tab.children.includes(childProp.config))) === null || _a === void 0 ? void 0 : _a.label;
                    return childProp.widget.getLabelValueAsTextPairs(childProp, joinLabels(labelPrefix, tabLabel));
                }
                else {
                    return [];
                }
            });
        }
        else {
            return [];
        }
    },
    childConfigs: (config) => config.tabs.flatMap((tab) => tab.children),
    component: WidgetFormTabs,
    icon: React.createElement(BorderAllIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormTabsEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, {
        type,
        key: 'tabs',
        tabs: [
            { label: 'Tab 1', children: [] },
            { label: 'Tab 2', children: [] },
        ],
    }),
};
